import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useMutation } from '@apollo/client';
import { Mutations } from 'apollo';
import { Redirect } from 'react-router';

import Grid from '@material-ui/core/Grid';
import HelpIcon from '@material-ui/icons/Help';
import ContentBox from 'components/CncUI/ContentBox';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';
import Link from 'components/CncUI/Link';

import ExpiredLink from 'images/ExpiredLink.svg';
import Loader from 'components/Loader';

import useSnackbar from 'hooks/useSnackbar';
import useGetUserStatus from '../hooks/useGetUserStatus';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ breakpoints, palette }) => ({
  box: {
    width: 462,
    padding: '40px 64px',
    margin: '36px auto 0',
    [breakpoints.down('sm')]: {
      width: 'auto',
      padding: '40px 20px',
    },
  },
  img: {
    marginBottom: -40,
  },
  snackbar: {
    '& .cncui-snackbar': {
      marginTop: 16,
      '& .cncui-snackbar-alert': {
        width: 453,
        height: 40,
      },
    },
  },
  emailAddress: {
    color: palette.cncUI.default.primary,
    fontWeight: 700,
  },
}));

const Error = props => {
  const classes = useStyles();
  const logger = Logger('accountCreation.error');
  const [sent, setSent] = useState(false);
  const [state, setState] = useState({ loading: true, redirect: false });
  const [sendActivationEmail] = useMutation(Mutations.resendActivationEmail);
  const getUserStatus = useGetUserStatus();
  const { state: stepState } = props;
  const { email } = stepState;
  const notify = useSnackbar();

  useEffect(() => {
    window.onbeforeunload = function() {};
    if (email) {
      getUserStatus(email)
        .then(status => {
          if (status === 'ACTIVE') {
            setState({ loading: false, redirect: true });
          } else {
            setState({ loading: false, redirect: false });
          }
        })
        .catch(err => {
          logger.error('Error Link: ', err);
          setState({ loading: false, redirect: false });
        });
    } else {
      setState({ loading: false, redirect: false });
    }
  }, []);

  if (state.loading) {
    return <Loader />;
  }

  if (state.redirect) {
    return <Redirect to="/login" />;
  }

  const send = () => {
    setSent(true);
    notify({
      type: 'success',
      message: 'A new link has been emailed',
      className: classes.snackbar,
    });
    const fromURI = stepState && stepState.fromURI;
    if (email) {
      sendActivationEmail({ variables: { email, fromURI } }).catch(err =>
        logger.error('Activation Email', err),
      );
    }
  };

  return (
    <ContentBox className={classes.box}>
      <Grid container spacing={2} direction="column" alignItems="center">
        <Grid item xs={12}>
          <img src={ExpiredLink} alt="Expired Link" className={classes.img} />
          <Typography variant="h4" noMargin>
            This link has expired
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" size="short">
            To receive a new link, click the button below.
          </Typography>
        </Grid>
        {email && (
          <Grid item xs={12}>
            <Typography variant="body2" size="short">
              Your email address:{' '}
              <span className={classes.emailAddress}>{email}</span>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button
            disabled={sent}
            onClick={() => send()}
            className={classes.button}
            id="ErrorSendNewLink"
          >
            Resend Activation Link
          </Button>
        </Grid>
        {sent && (
          <Grid item xs={12} style={{ width: '100%', marginTop: 40 }}>
            <Alert info icon={HelpIcon}>
              <Typography variant="h6" noMargin>
                Still haven’t received an email after 30 minutes?
              </Typography>
              <Typography size="short" noMargin>
                Visit{' '}
                <Link href="/support" target="_blank" rel="noopener noreferrer">
                  this page
                </Link>{' '}
                and submit a help ticket. One of our support team members will
                contact you.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </ContentBox>
  );
};

export default Error;
